import { API_URL } from "../config/config"
import { getRequest, patchRequest, postRequest } from "../utils/axiosUtils"
import { getDateFormat, getFilterQuery, getStatusLabel } from "../utils/helperFunction";

function mapObjectIdsDeep(obj) {
  if (Array.isArray(obj)) {
    return obj.map(item =>
      typeof item === 'object' && item !== null && 'value' in item ? item.value : mapObjectIdsDeep(item)
    );
  } else if (typeof obj === 'object' && obj !== null) {
    const newObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (
          Array.isArray(obj[key]) &&
          obj[key].length > 0 &&
          typeof obj[key][0] === 'object' &&
          obj[key][0] !== null &&
          'value' in obj[key][0]
        ) {
          newObj[key] = obj[key].map(item => item.value);
        } else {
          newObj[key] = mapObjectIdsDeep(obj[key]);
        }
      }
    }
    return newObj;
  }
  return obj;
}

export const saveCourse = async (courseData) => {
  if (!courseData) {
    throw new Error('Course data is required.');
  }

  const payload = {};
  courseData.forEach(field => {
    if (
      field.type === 'select' &&
      Array.isArray(field.value)
    ) {
      if (
        field.value.length > 0 &&
        typeof field.value[0] === 'object' &&
        field.value[0] !== null &&
        'value' in field.value[0]
      ) {
        payload[field.id] = field.value.map(option => option.value);
      } else {
        payload[field.id] = field.value;
      }
    }
    else if (
      field.type === 'select' &&
      typeof field.value === 'object' &&
      field.value !== null &&
      'value' in field.value
    ) {
      payload[field.id] = field.value.value;
    }
    else {
      payload[field.id] = field.value;
    }
  });
  const cleanedPayload = mapObjectIdsDeep(payload);

  try {
    const response = await postRequest({
      url: `${API_URL}/course/save`,
      data: cleanedPayload,
      headers: {
        'Content-Type': 'application/json'
      },
    });
    if (response?.data?.success === false) {
      const customError = new Error(response.data.message || 'Validation failed');
      customError.fieldErrors = response.data.errors || {};
      throw customError;
    }
    return response.data;
  } catch (err) {
    const responseData = err?.response?.data;
    if (responseData) {
      const customError = new Error(responseData.message || 'Course saving failed');
      customError.fieldErrors = responseData.errors || {};
      throw customError;
    }
    throw new Error(err.message || 'Unknown error occurred');
  }
};

export const updateCourseStatus = async (id, courseStatus, reason = '') => {
  if (!id || !courseStatus) {
    throw new Error('Course ID and status are required')
  }

  const response = await patchRequest({
    url: `${API_URL}/course/status/${id}`,
    data: { status: courseStatus, reason },
    headers: {
      'Content-Type': 'application/json',
    },
  })

  if (response?.data?.success === false) {
    throw new Error(response.data.message || 'Status update failed')
  }

  return response.data
}

export const fetchCourseNames = async (setCourseNames, filterData = {}) => {
  try {
    const query = getFilterQuery(filterData)
    const response = await getRequest({
      url: `${API_URL}/course/names${query}`,
      // data: filterData,
    })

    if (response.status >= 200 && response.status < 300 && response.data) {
      const options = response.data?.courseNames?.map(course => ({
        value: course._id,
        label: course.abbreviation
          ? `${course.name} (${course.abbreviation})`
          : course.name
      }))
      setCourseNames(options)
    } else {
      setCourseNames([])
      throw new Error(`Unexpected response code: ${response.status}`)
    }
  } catch (error) {
    setCourseNames([])
    console.error(error)
  }
}

export const fetchCoursesByUniversityId = async (universityId, setCourses, filterData = {}) => {
  if (!universityId) {
    setCourses([])
    throw new Error('University ID is required')
  }

  try {
    const response = await getRequest({
      url: `${API_URL}/course/university/${universityId}`,
      data: filterData,
    })

    if (response.status >= 200 && response.status < 300 && response.data) {
      const options = (response.data.courses || response.data).map(course => ({
        value: course.courseName?._id,
        label: course.courseName?.name,
        courseId: course._id
      }))
      setCourses(options)
    } else {
      setCourses([])
      throw new Error(`Unexpected response code: ${response.status}`)
    }
  } catch (error) {
    setCourses([])
    console.error(error)
  }
}

export const fetchCourseCategories = async (setCourseCategories, filterData = {}) => {
  try {
    const response = await getRequest({
      url: `${API_URL}/course/categories`,
      data: filterData,
    })

    if (response.status >= 200 && response.status < 300 && response.data) {
      const options = response.data.map(category => ({
        value: category._id,
        label: category.name
      }))
      setCourseCategories(options)
    } else {
      setCourseCategories([])
      throw new Error(`Unexpected response code: ${response.status}`)
    }
  } catch (error) {
    setCourseCategories([])
    console.error(error)
  }
}

export const fetchCourseList = async (
  setRows,
  filterData = {},
  userId = true,
  isReviewer = false,
  setRowCount
) => {
  try {
    const modifyFilterData = {...filterData,...(userId ? {isReviewer : isReviewer ? 'true' : 'false'} :[])}
    const query = getFilterQuery(modifyFilterData)
    const url = userId
      ? `${API_URL}/course/user${query}`
      : `${API_URL}/course/all${query}`;

    const response = await getRequest({
      url,
      // data: filterData,
    });

    if (response.status >= 200 && response.status < 300 && response.data) {
      const pagination = response.data?.pagination
      const options = (response.data.courses || []).map((course,idx) => ({
        sNo: (pagination?.page-1)*pagination.limit + (idx+1),
        id: course._id,
        courseId: course.courseId || '',
        course: course.courseName || '',
        courseCategory: course.courseCategory || '',
        university: course.university || '',
        universityLogo: course.universityLogo || '',
        universityId: course.universityId || '',
        status: (course.status || '')
          ? (course.status[0] || '').toUpperCase() + (course.status.slice(1) || '')
          : '',
        createdDate: course.createdAt ? new Date(course.createdAt).toLocaleDateString() : '',
        lastUpdate: course.updatedAt ? new Date(course.updatedAt).toLocaleDateString() : '',
        completion: course.completionPercentage || 0,
        createdBy: course.createdBy || '',
        reviewedBy: course.reviewedBy || '',
        comments: Array.isArray(course.faqsQuestions) && course.faqsQuestions.length > 0
          ? course.faqsQuestions.map(faq => faq.question).join(', ')
          : '',
      }));

      setRows(options);
      setRowCount && setRowCount(response.data?.pagination?.total || 0)
    } else {
      setRows([]);
      throw new Error(`Unexpected response code: ${response.status}`);
    }
  } catch (error) {
    setRows([]);
    console.error(error);
  }
};

export const fetchCourseByNameId = async (courseId, setCourse,filterData={},isReviewer,setRowCount) => {
  if (!courseId) {
    setCourse([])
    throw new Error('Course ID is required')
  }

  try {
    const query = getFilterQuery(filterData)
    const response = await getRequest({
      url: `${API_URL}/course/name/${courseId}${query}`,
    })

    if (
      response.status >= 200 &&
      response.status < 300 &&
      response.data &&
      Array.isArray(response.data.course)
    ) {
        const pagination = response.data?.pagination
      const formattedCourses = response.data.course.map((course,idx) => ({
         sNo: (pagination?.page-1)*pagination.limit + (idx+1),
        id: course._id,
        courseId: course.courseId,
        course: course?.courseNameId?.name,
        courseCategory: course?.courseCategory,
        university: course.universityId?.universityName,
        universityLogo: course.universityId?.universityLogo,
        universityId: course.universityId?.universityId,
        specializationCount: course?.specializationCount || 0,
        status: (course.status || '').charAt(0).toUpperCase() + (course.status || '').slice(1),
        createdDate: course.createdAt ? new Date(course.createdAt).toLocaleDateString() : '',
        lastUpdate: course.updatedAt ? new Date(course.updatedAt).toLocaleDateString() : '',
        completion: course.completionPercentage || 0,
        createdBy: course?.createdBy || '',
        reviewedBy: course?.reviewedBy || '',
        comments: Array.isArray(course.faqsQuestions) && course.faqsQuestions.length > 0
          ? course.faqsQuestions.map(faq => faq.question).join(', ')
          : '',
      }))

      setCourse(formattedCourses)
      setRowCount && setRowCount(response?.data?.pagination?.total)
    } else {
      setCourse([])
      setRowCount && setRowCount(0)
      throw new Error(`Unexpected response code: ${response.status}`)
    }
  } catch (error) {
    setCourse([])
    setRowCount && setRowCount(0)
    console.error(error)
  }
}

export const fetchCourseNameList = async (setRows, filterData = {}, userId = true,setRowCount) => {
  try {
    const query = getFilterQuery(filterData)
    const url = `${API_URL}/course/names${query}`
    const response = await getRequest({
      url,
      // data: filterData,
    })

    if (response.status >= 200 && response.status < 300 && response.data) {
       const pagination = response.data?.pagination
      const options = (response.data?.courseNames || []).map((course,idx) => ({
         sNo: (pagination?.page-1)*pagination.limit + (idx+1),
        id: course._id,
        course: course.name || '' ,
        courseCategory: course.category || '',
        courseCount: course.courseCount || 0,
        specializationCount: course.specializationCount || 0,
        tag: course.tag || 'uncategorized',
        isFooter: course.isFooter || false,
        isHomeVisible: course.isHomeVisible || false
      }))

      setRows(options)
      setRowCount && setRowCount(response.data?.pagination?.total || 0)
    } else {
      setRows([])
      throw new Error(`Unexpected response code: ${response.status}`)
    }
  } catch (error) {
    setRows([])
    console.error(error)
  }
}

export const fetchCourse = async (courseId, fieldTemplate = [],editMode) => {
  if (!courseId) {
    throw new Error('Course ID is required')
  }

  try {
    const response = await getRequest({
      url: `${API_URL}/course/${courseId}`,
    })

    const course = response?.data?.course

    if (!response?.data?.success || !course) {
      const error = new Error(response?.data?.message || 'Course not found')
      error.fieldErrors = response?.data?.errors || {}
      throw error
    }
    const isDraft = editMode || ['draft', 'rework'].includes(course.status);

    const populatedFields = fieldTemplate.map(field => {
      const rawValue = course[field.id]

      if (field.id === 'uploadRelevantDocument' && Array.isArray(rawValue)) {
        const documents = rawValue.map(doc => ({
          title: doc.title || '',
          file: null,
          uploading: false,
          url: doc.url || ''
        }))
        return {
          ...field,
          value: documents,
          readOnly: !isDraft
        }
      }
      if (
        field.id === 'emiFinancialAidForm' &&
        rawValue &&
        Array.isArray(rawValue.partnersId)
      ) {
        return {
          ...field,
          value: {
            ...rawValue,
            partnersId: rawValue.partnersId.map(partner => ({
              label: partner.title || partner.label || '',
              value: partner._id || partner.value || '1',
              image: partner.image || ''
            }))
          },
          readOnly: !isDraft
        }
      }

      if (field.type === 'select' && rawValue) {
        if (typeof rawValue === 'object') {
          return {
            ...field,
            value: {
              label: rawValue.label || rawValue.name || rawValue.value || '',
              value: rawValue._id || rawValue.value || '',
            },
            readOnly: !isDraft
          }
        } else {
          return {
            ...field,
            value: {
              label: String(rawValue),
              value: rawValue
            },
            readOnly: !isDraft
          }
        }
      }

      return {
        ...field,
        value: rawValue !== undefined ? rawValue : '',
        readOnly: !isDraft
      }
    })

    return populatedFields

  } catch (err) {
    const responseData = err?.response?.data
    if (responseData) {
      const customError = new Error(responseData.message || 'Course fetch failed')
      customError.fieldErrors = responseData.errors || {}
      throw customError
    }
    throw new Error(err.message || 'Unknown error occurred while fetching course')
  }
}

export const fetchCourseDashboardStats = async (setStatusList) => {
  try {
    const response = await getRequest({
      url: `${API_URL}/course/dashboard-stats`
    })

    if (response.status >= 200 && response.status < 300 && response.data?.data) {
      const stats = response.data.data

      const updatedList = [
        { label: 'Total Course', count: stats.totalCourses || 0 },
        { label: 'Total Variant Courses', count: stats.totalVariantCourses || 0 },
        { label: 'Archive Course', count: stats.archiveCourses || 0 },
        { label: 'Deactivated Course', count: stats.deactivatedCourses || 0 },
        { label: 'Saved in Draft', count: stats.savedInDraft || 0 }
      ]

      setStatusList(updatedList)
    } else {
      console.warn(`Unexpected response code: ${response.status}`)
      setStatusList([])
    }
  } catch (error) {
    console.error('Error fetching dashboard stats:', error)
    setStatusList([])
  }
}

export const fetchPartnerList = async (setPartnersList, setLoadingPartners) => {
  if (setLoadingPartners) setLoadingPartners(true)
  try {
    const response = await getRequest({
      url: `${API_URL}/course/financial/partner`,
    });
    const partners = response?.data?.data;
    if (!response?.data?.success || !Array.isArray(partners)) {
      const error = new Error(response?.data?.message || "Partners not found");
      error.fieldErrors = response?.data?.errors || {};
      throw error;
    }
    const mappedPartners = partners.map(partner => ({
      label: partner.title,
      value: partner._id,
      image: partner.image || "",
    }));
    if (setPartnersList) setPartnersList(mappedPartners)
  } catch (err) {
    if (setPartnersList) setPartnersList([])
    const responseData = err?.response?.data;
    if (responseData) {
      const customError = new Error(responseData.message || "Failed to fetch partners");
      customError.fieldErrors = responseData.errors || {};
      throw customError;
    }
    throw new Error(err.message || "Unknown error occurred while fetching partners");
  } finally {
    if (setLoadingPartners) setLoadingPartners(false)
  }
}

export const fetchCourseCategoryList = async (setList) => {
  try {
    const url = `${API_URL}/course-category`
    const response = await getRequest({ url,  })

    if (response.status >= 200 && response.status < 300 && response.data) {
      const options = (response.data?.data || []).map(item => ({
        label:item?.name,
        value:item?.name
      }))

      setList && setList(options)
    } else {
      setList && setList([])
      throw new Error(`Unexpected response code: ${response.status}`)
    }
  } catch (error) {
     setList && setList([])
    console.error(error)
  }
}

export const fetchCourseListByUniversityId = async ({universityId, setRows,filterData={},setRowCount}) => {
  if (!universityId) {
    setRows([])
    throw new Error('University ID is required')
  }
  let list = []
  let rowCount =0

  try {
    const query = getFilterQuery(filterData)
    const response = await getRequest({
      url: `${API_URL}/course/list/university/${universityId}${query}`,
    })

    if (
      response.data?.success && response.data &&
      Array.isArray(response.data.data)
    ) {
      const pagination = response.data?.pagination
       list = response.data.data.map((course,idx) => ({
        sNo: (pagination?.page-1)*pagination.limit + (idx+1),
        id: course._id,
        courseId: course.courseId,
        course: course?.courseNameId?.name,
        courseCategory: course?.courseCategory,
        university: course.universityId?.universityName,
        universityLogo: course.universityId?.universityLogo,
        universityId: course.universityId?.universityId,
        specializationCount: course?.specializationCount || 0,
        status: getStatusLabel(course?.status),
        createdDate: getDateFormat(course.createdAt),
        lastUpdate: getDateFormat(course.updatedAt),
        completion: course.completionPercentage || 0,
        createdBy: course?.createdBy?.fullName || '',
      }))

      rowCount = response?.data?.pagination?.total
    } else {

      throw new Error(`Unexpected response code: ${response.status}`)
    }
  } catch (error) {
    console.error(error)
    throw new Error(error?.response?.data?.message || `Something went wrong`)
  }finally{
    setRows(list)
    setRowCount && setRowCount(rowCount)
  }
}